import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for mobile */
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(204, 51, 51, 0.25));
  padding: clamp(var(--spacing-4), 4vw, var(--spacing-8));
  padding-top: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-top));
  padding-bottom: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-4);
    padding-top: max(var(--spacing-4), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-4), env(safe-area-inset-bottom));
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3);
    padding-top: max(var(--spacing-3), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-3), env(safe-area-inset-bottom));
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: clamp(var(--radius-lg), 2vw, 1rem);
  padding: clamp(var(--spacing-6), 4vw, var(--spacing-10));
  color: #fff;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    max-width: 100%;
    padding: var(--spacing-6);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-5);
    border-radius: var(--radius-md);
  }
`;

const Title = styled.h1`
  font-size: clamp(var(--font-size-xl), 3vw, 1.75rem);
  margin-bottom: 0.5rem;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: var(--font-size-lg);
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: clamp(0.75rem, 2vw, 0.85rem) clamp(0.875rem, 2vw, 1rem);
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  box-sizing: border-box;
  -webkit-appearance: none; /* Remove iOS styling */
  appearance: none;

  &:focus {
    outline: none;
    border-color: #cc3333;
    background: rgba(0, 0, 0, 0.35);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Button = styled.button`
  margin-top: 0.5rem;
  padding: clamp(0.875rem, 2vw, 0.95rem);
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: none;
  background: #cc3333;
  color: #fff;
  font-weight: 600;
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #b02b2b;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98); /* Touch feedback */
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const ErrorText = styled.p`
  color: #ff8080;
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
`;

const SwitchText = styled.p`
  margin-top: 1.25rem;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
  font-size: 0.95rem;
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  margin-left: 0.35rem;

  &:hover {
    text-decoration: underline;
  }
`;

interface RegisterPageProps {
  onSwitchToLogin: () => void;
}

const SuccessMessage = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  color: #90ee90;
  font-size: 0.95rem;
  line-height: 1.5;
`;

export const RegisterPage: React.FC<RegisterPageProps> = ({ onSwitchToLogin }) => {
  const { register, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setLocalError(null);
    setRegistrationSuccess(false);
    clearError();
    try {
      const result = await register(email, password);
      // If register returns a user object, it means email is not verified
      if (result && !result.email_verified) {
        setRegistrationSuccess(true);
        // After 3 seconds, switch to login page
        setTimeout(() => {
          onSwitchToLogin();
        }, 3000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to register';
      setLocalError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <Title>Create your account</Title>
        <Subtitle>Join Vermilion and get access to advanced scouting tools</Subtitle>

        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
          />
          <Input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
          />

          {registrationSuccess && (
            <SuccessMessage>
              ✅ Account created successfully!<br />
              Please check your email to verify your account.<br />
              Redirecting to login...
            </SuccessMessage>
          )}

          {(localError || error) && <ErrorText>{localError || error}</ErrorText>}

          <Button type="submit" disabled={submitting || registrationSuccess}>
            {submitting ? 'Creating account...' : 'Create Account'}
          </Button>
        </Form>

        <SwitchText>
          Already have an account?
          <SwitchButton type="button" onClick={onSwitchToLogin}>
            Sign in
          </SwitchButton>
        </SwitchText>
      </Card>
    </PageContainer>
  );
};




